import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import { useEffect, useState } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from './features/auth/authSlice';
import { jwtDecode } from 'jwt-decode';
import { getProducts } from './features/product/productSlice';
import Header from './components/Header';
import AddProduct from './pages/admin/AddProduct';
import AdminProducts from './pages/admin/AdminProducts';
import AddArtwork from './pages/admin/AddArtwork';
import AdminArtworks from './pages/admin/AdminArtworks';
import Home from './pages/Home';
import About from './pages/About';
import Shop from './pages/Shop';
import Product from './pages/Product';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderInspect from './pages/admin/AdminOrderInspect';
import MarketSelector from './components/MarketSelector';
import Cookies from './components/Cookies';
import Footer from './components/Footer';
import CountdownOverlay from './components/CountdownOverlay';
import { PORTFOLIO_DOWNLOAD_FILENAME, PORTFOLIO_DOWNLOAD_PATH } from './constants/portfolio';
import { initAnalytics, trackClick, trackEvent, trackPageView } from './utils/analytics';

// --- Constants ---
const MARKET_STORAGE_KEY = 'liviiart_market';

// --- Helper Components ---

/**
 * A simple wrapper component that applies the 'container' class.
 * This is now used directly as the element for container-needed routes.
 */
function ContainerWrapper({ children }) {
    return <div className="container">{children}</div>;
}

/**
 * Checks for token expiration every 5 seconds and logs out the user if expired.
 */
function TokenExpirationHandler() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (!authUser || !authUser.token) return;

    const checkTokenExpiration = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user')) || authUser;
        if (user && user.token) {
          const decoded = jwtDecode(user.token);
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            dispatch(logout());
            navigate('/login');
          }
        }
      } catch (err) {
        console.error('Token decode failed', err);
        dispatch(logout());
        navigate('/login');
      }
    };

    // Run immediately and then poll.
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 5000);
    return () => clearInterval(interval);
  }, [authUser, dispatch, navigate]);

  return null;
}

/**
 * Handles the logout action and redirects to the login page.
 */
function LogoutHandler() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(logout());
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  return null;
}

// --- Main App Component ---

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return null;
}

function Analytics({ cookiesAccepted }) {
  const location = useLocation();

  useEffect(() => {
    if (!cookiesAccepted || !initAnalytics()) return;

    trackPageView(`${location.pathname}${location.search}`);
  }, [cookiesAccepted, location.pathname, location.search]);

  useEffect(() => {
    if (!cookiesAccepted) return undefined;

    document.addEventListener('click', trackClick);
    return () => document.removeEventListener('click', trackClick);
  }, [cookiesAccepted]);

  return null;
}

function PortfolioDownloadRoute() {
  const navigate = useNavigate();

  useEffect(() => {
    const downloadLink = document.createElement('a');
    downloadLink.href = PORTFOLIO_DOWNLOAD_PATH;
    downloadLink.download = PORTFOLIO_DOWNLOAD_FILENAME;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    trackEvent('portfolio_download', {
      file_name: PORTFOLIO_DOWNLOAD_FILENAME,
    });

    navigate('/', { replace: true });
  }, [navigate]);

  return <Home />;
}

function App() {
  const [cookiesAccepted, setCookiesAccepted] = useState(() => {
    return localStorage.getItem('cookiesAccepted') === 'true';
  });
  const [market, setMarket] = useState(() => sessionStorage.getItem(MARKET_STORAGE_KEY));
  const [showMarketSelector, setShowMarketSelector] = useState(!market);
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);

  const handleMarketSelect = (value) => {
    sessionStorage.setItem(MARKET_STORAGE_KEY, value);
    setMarket(value);
    setShowMarketSelector(false);
    trackEvent('select_market', {
      market: value,
    });
  };

  const handleAcceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setCookiesAccepted(true);
  };

  useEffect(() => {
    if (!market) {
      setShowMarketSelector(true);
      return;
    }

    setShowMarketSelector(false);
    
    if (!authUser){
      dispatch(getProducts(market));
    }
      
  }, [dispatch, market]);

  
  return (
    <>
      <Router>
        <ScrollToTop />
        <Analytics cookiesAccepted={cookiesAccepted} />
        <AppContent
          cookiesAccepted={cookiesAccepted}
          onAcceptCookies={handleAcceptCookies}
          showMarketSelector={showMarketSelector}
          market={market}
          onSelectMarket={handleMarketSelect}
        />
      </Router>
      <ToastContainer
        className="toast-position"
        autoClose={2000}
        style={{ top: '90px' }}
      />
    </>
  );
}

function AppContent({
  cookiesAccepted,
  onAcceptCookies,
  showMarketSelector,
  market,
  onSelectMarket,
}) {
  const location = useLocation();
  const isOrderSuccessPage = location.pathname.startsWith('/order/success');
  const authUser = useSelector((state) => state.auth.user);
  const isLoginRoute = location.pathname === '/login';
  const shouldShowCountdownOverlay = !authUser && !isLoginRoute;

  return (
    <div className="app-shell">
      <TokenExpirationHandler />
      <Header />

      {!cookiesAccepted && !isOrderSuccessPage && (
        <Cookies onAccept={onAcceptCookies} />
      )}

      <main className='app-content'>
        <Routes>
          {/* 1. Routes WITHOUT the container (e.g., full-width pages) */}
          <Route path='/' element={<Home />} />
          <Route path='/about' element={<About />} />
          <Route path='/shop' element={<Shop />} />
          <Route path='/portfolio' element={<PortfolioDownloadRoute />} />

          {/* 2. Routes that NEED the container, now explicitly wrapping the element */}
          <Route path='/login' element={<ContainerWrapper><Login /></ContainerWrapper>} />
          <Route path='/product/:id' element={<ContainerWrapper><Product /></ContainerWrapper>} />
          <Route path='/cart' element={<ContainerWrapper><Cart /></ContainerWrapper>} />
          <Route path='/checkout' element={<ContainerWrapper><Checkout /></ContainerWrapper>} />
          <Route path='/order/success' element={<ContainerWrapper><OrderSuccess /></ContainerWrapper>} />
          <Route path='/logout' element={<LogoutHandler />} />

          <Route path='/admin/add-product' element={<ContainerWrapper><AddProduct /></ContainerWrapper>} />
          <Route path='/admin/add-product/:id' element={<ContainerWrapper><AddProduct /></ContainerWrapper>} />
          <Route path='/admin/products' element={<ContainerWrapper><AdminProducts /></ContainerWrapper>} />
          <Route path='/admin/products/trash' element={<ContainerWrapper><AdminProducts /></ContainerWrapper>} />
          <Route path='/admin/add-artwork' element={<ContainerWrapper><AddArtwork /></ContainerWrapper>} />
          <Route path='/admin/add-artwork/:id' element={<ContainerWrapper><AddArtwork /></ContainerWrapper>} />
          <Route path='/admin/artworks' element={<ContainerWrapper><AdminArtworks /></ContainerWrapper>} />
          <Route path='/admin/artworks/trash' element={<ContainerWrapper><AdminArtworks /></ContainerWrapper>} />
          <Route path='/admin/orders' element={<ContainerWrapper><AdminOrders /></ContainerWrapper>} />
          <Route path='/admin/orders/:orderId' element={<ContainerWrapper><AdminOrderInspect /></ContainerWrapper>} />

          {/* 3. Catch-all */}
          <Route path='*' element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {showMarketSelector && !market && !isOrderSuccessPage && (
        <MarketSelector onSelect={onSelectMarket} />
      )}

      <Footer />
      {/* <CountdownOverlay isVisible={shouldShowCountdownOverlay} /> */}
    </div>
  );
}

export default App;
